import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Loader2, Save, Wand2, TriangleAlert, X, Eye, EyeOff } from 'lucide-react';
import { planningService } from '../../services/planningService';
import MantleDataGrid, { gcell } from '../../components/MantleDataGrid';
import { PERIODS, periodLabel } from '../../utils/financialPeriods';
import usePeriodDrilldown, { periodColumns, periodsOfCol, periodHeaderToggle, aggPeriods } from '../../hooks/usePeriodDrilldown';

// =============================================================================
// Planning > lane volumes (popup)
//
// One question only: how much of each SKU moves from this origin to this
// destination, per period. No vehicles, no distances, no rates — whether a
// truck comes back empty is a distribution decision made on the rating page,
// and putting it here made a planning screen look like a costing screen.
//
// The supply pool stays available but OFF by default. A lane cannot truly be
// planned in isolation — the origin's stock is shared with its other lanes and
// its own local sales — so the four grey columns show what is left to give:
//
//   Available  production + inbound at the origin
//   Committed  outbound on the origin's other lanes
//   Local      the origin's own sales demand
//   Free       available - committed - local - this lane
//
// A negative Free is a lane the plan cannot supply. It is a toggle rather than
// always-on because most of the time the planner is simply keying numbers.
// =============================================================================

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const sum = (a) => a.reduce((x, v) => x + (Number(v) || 0), 0);

const T_POOL = { bgCell: 'rgba(100, 116, 139, 0.08)', textDark: '#64748b' };
const T_SHORT = { bgCell: 'rgba(220, 38, 38, 0.12)', textDark: '#b91c1c' };
const T_TOTAL = { bgCell: 'rgba(100, 116, 139, 0.10)', baseFontStyle: '600 12px' };

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';

export default function LaneVolumesModal({ laneId, readOnly, fyStartMonth, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lane, setLane] = useState(null);
  const [rows, setRows] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [showPool, setShowPool] = useState(false);
  const [poolPeriod, setPoolPeriod] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await planningService.getLaneVolumes(laneId);
      const d = res.data || {};
      setLane(d.lane || null);
      setRows((d.rows || []).map((r) => ({ ...r, lane: [...r.lane] })));
      setDirty(false);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load the lane.');
    } finally {
      setLoading(false);
    }
  }, [laneId]);

  useEffect(() => { load(); }, [load]);

  const pool = useMemo(() => rows.map((r) => {
    const i = poolPeriod - 1;
    const available = (r.production[i] || 0) + (r.inbound[i] || 0);
    const committed = r.committed[i] || 0;
    const local = r.local_demand[i] || 0;
    return { available, committed, local, free: available - committed - local - (r.lane[i] || 0) };
  }), [rows, poolPeriod]);

  const totals = useMemo(() => {
    const perPeriod = PERIODS.map((_, i) => rows.reduce((a, r) => a + (Number(r.lane[i]) || 0), 0));
    return { perPeriod, year: sum(perPeriod) };
  }, [rows]);

  const shortPeriods = useMemo(() => PERIODS.filter((p) => {
    const i = p - 1;
    return rows.some((r) => (r.production[i] || 0) + (r.inbound[i] || 0)
      - (r.committed[i] || 0) - (r.local_demand[i] || 0) - (r.lane[i] || 0) < -0.5);
  }), [rows]);

  // Column layout shifts with the toggle, so the period offset is derived
  // rather than hard-coded — getting this wrong writes into the wrong month.
  const poolCols = showPool ? 4 : 0;
  const firstPeriodCol = 2 + poolCols;

  // Periods drill down — Full Year → quarters → months, same as OPEX entry.
  // Lane volumes are quantities, so a group column SUMS its months.
  const drill = usePeriodDrilldown(PERIODS);

  const columns = useMemo(() => ([
    { id: 'sku', title: 'SKU', width: 150 },
    { id: 'desc', title: 'Description', width: 200 },
    ...(showPool ? [
      { id: 'available', title: 'Available', width: 90 },
      { id: 'committed', title: 'Committed', width: 92 },
      { id: 'local', title: 'Local', width: 78 },
      { id: 'free', title: 'Free', width: 86 },
    ] : []),
    ...periodColumns(drill, fyStartMonth, { monthWidth: 74, groupWidth: 108 }),
    { id: 'year', title: 'Year', width: 96 },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]), [showPool, fyStartMonth, drill.visibleCols, drill.yearExpanded, drill.expandedQuarters]);

  const getCellContent = useCallback(([col, row]) => {
    // Period columns are addressed by ID, never by offset: they collapse into
    // quarters and a year now, so a hard-coded position writes into the wrong
    // month the moment anything is folded.
    const ps = periodsOfCol(columns, columns[col]?.id);

    if (row === rows.length) {
      if (col === 0) return gcell.text('TOTAL', { readonly: true, theme: T_TOTAL });
      if (col === columns.length - 1) return gcell.number(totals.year, { readonly: true, display: fmt(totals.year), theme: T_TOTAL });
      if (!ps) return gcell.text('', { readonly: true, theme: T_TOTAL });
      const t = aggPeriods(ps, (p) => totals.perPeriod[p - 1]);
      return gcell.number(t, { readonly: true, display: fmt(t), theme: T_TOTAL });
    }

    const r = rows[row];
    if (!r) return gcell.text('');
    if (col === 0) return gcell.text(r.stock_code, { readonly: true });
    if (col === 1) return gcell.text(r.stock_description, { readonly: true, theme: T_POOL });

    if (showPool && col < firstPeriodCol) {
      const pl = pool[row];
      if (col === 2) return gcell.number(pl.available, { readonly: true, display: fmt(pl.available), theme: T_POOL });
      if (col === 3) return gcell.number(pl.committed, { readonly: true, display: fmt(pl.committed), theme: T_POOL });
      if (col === 4) return gcell.number(pl.local, { readonly: true, display: fmt(pl.local), theme: T_POOL });
      return gcell.number(pl.free, { readonly: true, display: fmt(pl.free), theme: pl.free < -0.5 ? T_SHORT : T_POOL });
    }

    if (col === columns.length - 1) {
      const y = sum(r.lane);
      return gcell.number(y, { readonly: true, display: fmt(y), theme: T_TOTAL });
    }
    if (!ps) return gcell.text('', { readonly: true });
    const v = aggPeriods(ps, (p) => r.lane[p - 1]);
    // One month = typed. A quarter or the year = an aggregate, read-only.
    return gcell.number(v, { readonly: readOnly || ps.length > 1, display: fmt(v), theme: ps.length > 1 ? T_TOTAL : undefined });
  }, [rows, pool, totals, columns, readOnly, showPool, firstPeriodCol]);

  const onCellEdited = useCallback(([col, row], newValue) => {
    if (readOnly || row >= rows.length) return;
    const ps = periodsOfCol(columns, columns[col]?.id);
    if (ps?.length !== 1) return; // months only
    const p = ps[0] - 1;
    const n = Number(newValue?.data);
    if (Number.isNaN(n)) return;
    setRows((prev) => prev.map((r, i) => {
      if (i !== row) return r;
      const lanes = [...r.lane];
      lanes[p] = n;
      return { ...r, lane: lanes };
    }));
    setDirty(true);
  }, [rows.length, readOnly, columns]);

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const res = await planningService.saveLaneVolumes(
        laneId,
        rows.map((r) => ({ stock_code: r.stock_code, quantities: r.lane, unit_of_measure: r.unit_of_measure || null }))
      );
      onSaved(res.message || 'Lane volumes saved.');
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const seed = async (mode) => {
    setBusy(true);
    setError('');
    try {
      await planningService.seedLaneVolumes(laneId, mode);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-6" onClick={onClose}>
      <div className="w-full max-w-6xl h-[85vh] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="shrink-0 flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <h2 className="m-0 text-base font-bold text-slate-900 dark:text-slate-100 truncate">
              {lane ? `${lane.origin_name} → ${lane.dest_name}` : 'Lane volumes'}
            </h2>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              How much of each SKU moves down this lane, per period. Quantities are in each SKU&rsquo;s own unit.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button className={btn} onClick={() => setShowPool((s) => !s)} title="Show what the origin has left to give">
              {showPool ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} Supply at origin
            </button>
            <button className={btn} onClick={() => seed('fill')} disabled={readOnly || busy}
                    title="Fill only the SKU/periods that are currently empty">
              <Wand2 className="w-3.5 h-3.5" /> Fill gaps
            </button>
            <button className={btn} onClick={() => seed('replace')} disabled={readOnly || busy}
                    title="Clear the lane and pull it fresh from destination demand">
              <Wand2 className="w-3.5 h-3.5" /> Re-pull
            </button>
            <button className={btnPrimary} onClick={save} disabled={readOnly || busy || !dirty}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
            <button onClick={onClose} className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {(error || shortPeriods.length > 0 || (lane && !lane.origin_holds_stock)) && (
          <div className="shrink-0 px-5 pt-3 space-y-2">
            {error && (
              <div className="px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
                <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}
            {shortPeriods.length > 0 && (
              <div className="flex items-start gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
                <TriangleAlert className="w-4 h-4 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
                <p className="m-0 text-xs text-red-700 dark:text-red-300">
                  {lane?.origin_name} is over-committed in{' '}
                  <strong>{shortPeriods.map((p) => periodLabel(p, fyStartMonth)).join(', ')}</strong> — more is leaving
                  than production and inbound can supply.
                </p>
              </div>
            )}
            {lane && !lane.origin_holds_stock && (
              <div className="px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
                <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
                  {lane.origin_name} is a cross-dock — it holds no stock, so whatever arrives should leave the same period.
                </p>
              </div>
            )}
          </div>
        )}

        {showPool && (
          <div className="shrink-0 px-5 pt-3 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Supply shown for</span>
            {PERIODS.map((p) => (
              <button key={p} onClick={() => setPoolPeriod(p)}
                className={`px-2 py-1 text-[11px] font-semibold rounded-md border transition-colors ${poolPeriod === p
                  ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300'
                  : 'border-slate-200 dark:border-slate-600 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}>
                {periodLabel(p, fyStartMonth)}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 min-h-0 p-5 flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-center">
              <p className="m-0 text-sm text-slate-500 dark:text-slate-400">Nothing to plan on this lane yet.</p>
              <p className="m-0 text-xs text-slate-400 max-w-md">
                No production or inbound at {lane?.origin_name}, and no demand at {lane?.dest_name}, for any SKU.
                Load the volume plan first, then re-pull.
              </p>
            </div>
          ) : (
            <div className="flex-1 min-h-0 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
              <MantleDataGrid
                columns={columns}
                rowCount={rows.length + 1}
                getCellContent={getCellContent}
                onCellEdited={onCellEdited}
                onHeaderClicked={(col) => periodHeaderToggle(drill, columns[col]?.id)}
                freezeColumns={2}
                gridId={`planning-lane-${showPool ? 'pool' : 'plain'}`}
                hint="Click Full Year → quarters → months to drill into the periods. Paste a block straight from Excel into the month columns."
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
